import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import { Spinner } from '@/components/ui/Spinner'
import { Badge } from '@/components/ui/Badge'
import { useCreateProject } from '@/hooks/useProjects'
import { useClients } from '@/hooks/useClients'
import { useAuth } from '@/contexts/AuthContext'
import { useAutoSave } from '@/hooks/useAutoSave'
import { projectAIService } from '@/services/projectAI'
import { supabase } from '@/lib/supabase'
import {
  INITIAL_QUESTIONS,
  PROJECT_TYPE_LABELS,
  PROJECT_TYPE_DESCRIPTIONS,
  type ProjectType,
  type ProjectQuestion,
} from '@/config/project-questions'
import {
  ArrowLeft,
  ArrowRight,
  FolderKanban,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Save,
} from 'lucide-react'

type Step = 'type' | 'initial_questions' | 'dynamic_questions' | 'summary'

interface ProjectFormState {
  step: Step
  project_type: ProjectType | ''
  client_id: string
  answers: Record<string, unknown>
  current_iteration: number
  dynamic_questions: ProjectQuestion[]
  completeness_score: number
  bmad_markdown: string
}

export default function NewProjectPageWithAI() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const createProject = useCreateProject()
  const { data: clientsData, isLoading: loadingClients } = useClients({ limit: 1000 })

  const [state, setState] = useState<ProjectFormState>({
    step: 'type',
    project_type: '',
    client_id: '',
    answers: {},
    current_iteration: 1,
    dynamic_questions: [],
    completeness_score: 0,
    bmad_markdown: '',
  })

  const [draftId, setDraftId] = useState<string | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [isGeneratingBMAD, setIsGeneratingBMAD] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Auto-save
  const { lastSaved, isSaving } = useAutoSave({
    projectRequestId: draftId,
    data: {
      ...state,
      user_id: user?.id,
    },
    interval: 30000, // 30 secondes
    disabled: state.step === 'type' || !draftId,
    localStorageKey: 'project_creation_draft',
  })

  // Créer un brouillon au début
  useEffect(() => {
    const createDraft = async () => {
      if (!user || draftId || state.step === 'type') return

      const { data, error } = await supabase
        .from('client_requests')
        .insert({
          user_id: user.id,
          request_type: 'creation_projet',
          status: 'draft',
          data: state,
          is_saved_draft: true,
        })
        .select('id')
        .single()

      if (!error && data) {
        setDraftId(data.id)
      }
    }

    createDraft()
  }, [user, state.step, draftId, state])

  // Étape 1: Sélection du type
  const handleSelectType = (type: ProjectType) => {
    setState((prev) => ({
      ...prev,
      project_type: type,
      step: 'initial_questions',
    }))
  }

  // Étape 2: Répondre aux questions initiales
  const handleAnswerQuestion = (questionId: string, answer: unknown) => {
    setState((prev) => ({
      ...prev,
      answers: {
        ...prev.answers,
        [questionId]: answer,
      },
    }))
  }

  // Validation des questions initiales
  const canProceedFromInitial = () => {
    if (!state.project_type) return false
    const questions = INITIAL_QUESTIONS[state.project_type]
    const requiredQuestions = questions.filter((q) => q.required)
    return requiredQuestions.every((q) => state.answers[q.id])
  }

  // Passer à l'analyse IA
  const handleAnalyzeWithAI = async () => {
    if (!state.project_type) return

    setIsAnalyzing(true)
    setError(null)

    try {
      const result = await projectAIService.analyzeProjectRequest(
        state.project_type,
        state.answers,
        state.current_iteration
      )

      setState((prev) => ({
        ...prev,
        dynamic_questions: result.next_questions,
        completeness_score: result.completeness_score,
        step: result.is_complete ? 'summary' : 'dynamic_questions',
      }))
    } catch (err) {
      setError('Erreur lors de l\'analyse IA. Veuillez réessayer.')
      console.error(err)
    } finally {
      setIsAnalyzing(false)
    }
  }

  // Itération suivante
  const handleNextIteration = async () => {
    if (!state.project_type) return

    setIsAnalyzing(true)
    setError(null)

    try {
      const result = await projectAIService.analyzeProjectRequest(
        state.project_type,
        state.answers,
        state.current_iteration + 1,
        state.dynamic_questions
      )

      setState((prev) => ({
        ...prev,
        dynamic_questions: result.next_questions,
        completeness_score: result.completeness_score,
        current_iteration: prev.current_iteration + 1,
        step: result.is_complete ? 'summary' : 'dynamic_questions',
      }))
    } catch (err) {
      setError('Erreur lors de l\'analyse IA.')
    } finally {
      setIsAnalyzing(false)
    }
  }

  // Passer au résumé
  const handleGoToSummary = async () => {
    if (!state.project_type) return

    setIsGeneratingBMAD(true)
    setError(null)

    try {
      const result = await projectAIService.generateBMADDocument(
        state.project_type,
        state.answers
      )

      setState((prev) => ({
        ...prev,
        bmad_markdown: result.bmad_markdown,
        step: 'summary',
      }))
    } catch (err) {
      setError('Erreur lors de la génération du document BMAD.')
    } finally {
      setIsGeneratingBMAD(false)
    }
  }

  // Soumission finale
  const handleSubmit = async () => {
    if (!state.client_id || !state.project_type) {
      setError('Veuillez sélectionner un client.')
      return
    }

    try {
      const project = await createProject.mutateAsync({
        nom: state.answers.project_title as string || `Projet ${PROJECT_TYPE_LABELS[state.project_type]}`,
        description: state.bmad_markdown || JSON.stringify(state.answers),
        status: 'pending',
        client_id: state.client_id,
        admin_id: user?.id || null,
        budget: state.answers.budget as number || null,
        date_debut: null,
        date_fin: state.answers.deadline as string || null,
      })

      // Supprimer le brouillon
      if (draftId) {
        await supabase
          .from('client_requests')
          .delete()
          .eq('id', draftId)
      }

      localStorage.removeItem('project_creation_draft')
      navigate(`/projects/${project.id}`)
    } catch (err) {
      setError('Erreur lors de la création du projet.')
      console.error(err)
    }
  }

  // Rendu du contenu selon l'étape
  const renderStepContent = () => {
    switch (state.step) {
      case 'type':
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-brown-900 mb-2">
                Quel type de projet souhaitez-vous créer ?
              </h2>
              <p className="text-brown-600">
                Sélectionnez le type qui correspond le mieux à votre projet
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(PROJECT_TYPE_LABELS).map(([type, label]) => (
                <button
                  key={type}
                  onClick={() => handleSelectType(type as ProjectType)}
                  className="p-6 border-2 border-beige-200 rounded-lg hover:border-mint-500 hover:bg-mint-50 transition-all text-left group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-mint-100 flex items-center justify-center group-hover:bg-mint-200 transition-colors">
                      <FolderKanban className="w-6 h-6 text-mint-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-brown-900 mb-1">{label}</h3>
                      <p className="text-sm text-brown-600">
                        {PROJECT_TYPE_DESCRIPTIONS[type as ProjectType]}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )

      case 'initial_questions':
        if (!state.project_type) return null
        const initialQuestions = INITIAL_QUESTIONS[state.project_type]

        return (
          <div className="space-y-6">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-brown-900 mb-2">
                Questions initiales - {PROJECT_TYPE_LABELS[state.project_type]}
              </h2>
              <p className="text-brown-600">
                Répondez à ces questions pour que l'IA puisse vous aider à affiner votre projet
              </p>
            </div>

            <div className="space-y-6">
              {initialQuestions.map((question) => (
                <div key={question.id} className="space-y-2">
                  <label className="text-sm font-medium text-brown-700">
                    {question.question} {question.required && <span className="text-red-500">*</span>}
                  </label>

                  {question.type === 'textarea' && (
                    <textarea
                      value={(state.answers[question.id] as string) || ''}
                      onChange={(e) => handleAnswerQuestion(question.id, e.target.value)}
                      placeholder={question.placeholder}
                      rows={4}
                      className="w-full px-3 py-2 border border-beige-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mint-500"
                    />
                  )}

                  {question.type === 'text' && (
                    <Input
                      value={(state.answers[question.id] as string) || ''}
                      onChange={(e) => handleAnswerQuestion(question.id, e.target.value)}
                      placeholder={question.placeholder}
                    />
                  )}

                  {question.type === 'multiple_choice' && (
                    <Select
                      value={(state.answers[question.id] as string) || ''}
                      onChange={(e) => handleAnswerQuestion(question.id, e.target.value)}
                    >
                      <option value="">Sélectionnez une option</option>
                      {question.options?.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Select>
                  )}

                  {question.type === 'checkbox_multiple' && (
                    <div className="space-y-2">
                      {question.options?.map((option) => (
                        <label key={option} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(state.answers[question.id] as string[] || []).includes(option)}
                            onChange={(e) => {
                              const current = (state.answers[question.id] as string[]) || []
                              const updated = e.target.checked
                                ? [...current, option]
                                : current.filter((v) => v !== option)
                              handleAnswerQuestion(question.id, updated)
                            }}
                            className="w-4 h-4 text-mint-600 border-beige-300 rounded focus:ring-mint-500"
                          />
                          <span className="text-brown-900">{option}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {question.type === 'yes_no' && (
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name={question.id}
                          checked={state.answers[question.id] === 'yes'}
                          onChange={() => handleAnswerQuestion(question.id, 'yes')}
                          className="w-4 h-4 text-mint-600 border-beige-300 focus:ring-mint-500"
                        />
                        <span className="text-brown-900">Oui</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name={question.id}
                          checked={state.answers[question.id] === 'no'}
                          onChange={() => handleAnswerQuestion(question.id, 'no')}
                          className="w-4 h-4 text-mint-600 border-beige-300 focus:ring-mint-500"
                        />
                        <span className="text-brown-900">Non</span>
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end pt-6 border-t border-beige-200">
              <Button
                onClick={handleAnalyzeWithAI}
                disabled={!canProceedFromInitial() || isAnalyzing}
                leftIcon={isAnalyzing ? <Spinner size="sm" /> : <Sparkles className="w-4 h-4" />}
              >
                {isAnalyzing ? 'Analyse en cours...' : 'Analyser avec l\'IA'}
              </Button>
            </div>
          </div>
        )

      case 'dynamic_questions':
        return (
          <div className="space-y-6">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-brown-900">
                    Questions complémentaires (Itération {state.current_iteration})
                  </h2>
                  <p className="text-brown-600">
                    L'IA a généré des questions pour mieux comprendre votre projet
                  </p>
                </div>
                <Badge variant={state.completeness_score > 0.7 ? 'mint' : 'gold'}>
                  {Math.round(state.completeness_score * 100)}% complet
                </Badge>
              </div>
            </div>

            <div className="space-y-6">
              {state.dynamic_questions.map((question) => (
                <div key={question.id} className="space-y-2">
                  <label className="text-sm font-medium text-brown-700">
                    {question.question}
                  </label>

                  {question.type === 'textarea' && (
                    <textarea
                      value={(state.answers[question.id] as string) || ''}
                      onChange={(e) => handleAnswerQuestion(question.id, e.target.value)}
                      placeholder={question.placeholder}
                      rows={4}
                      className="w-full px-3 py-2 border border-beige-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mint-500"
                    />
                  )}

                  {question.type === 'multiple_choice' && (
                    <Select
                      value={(state.answers[question.id] as string) || ''}
                      onChange={(e) => handleAnswerQuestion(question.id, e.target.value)}
                    >
                      <option value="">Sélectionnez une option</option>
                      {question.options?.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Select>
                  )}

                  {question.type === 'checkbox_multiple' && (
                    <div className="space-y-2">
                      {question.options?.map((option) => (
                        <label key={option} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(state.answers[question.id] as string[] || []).includes(option)}
                            onChange={(e) => {
                              const current = (state.answers[question.id] as string[]) || []
                              const updated = e.target.checked
                                ? [...current, option]
                                : current.filter((v) => v !== option)
                              handleAnswerQuestion(question.id, updated)
                            }}
                            className="w-4 h-4 text-mint-600 border-beige-300 rounded focus:ring-mint-500"
                          />
                          <span className="text-brown-900">{option}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {question.type === 'yes_no' && (
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name={question.id}
                          checked={state.answers[question.id] === 'yes'}
                          onChange={() => handleAnswerQuestion(question.id, 'yes')}
                          className="w-4 h-4 text-mint-600 border-beige-300 focus:ring-mint-500"
                        />
                        <span className="text-brown-900">Oui</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name={question.id}
                          checked={state.answers[question.id] === 'no'}
                          onChange={() => handleAnswerQuestion(question.id, 'no')}
                          className="w-4 h-4 text-mint-600 border-beige-300 focus:ring-mint-500"
                        />
                        <span className="text-brown-900">Non</span>
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-between pt-6 border-t border-beige-200">
              <Button variant="outline" onClick={handleGoToSummary} disabled={isGeneratingBMAD}>
                Passer au résumé
              </Button>
              <Button
                onClick={handleNextIteration}
                disabled={isAnalyzing}
                leftIcon={isAnalyzing ? <Spinner size="sm" /> : <ArrowRight className="w-4 h-4" />}
              >
                {isAnalyzing ? 'Analyse...' : 'Continuer l\'analyse'}
              </Button>
            </div>
          </div>
        )

      case 'summary':
        return (
          <div className="space-y-6">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-brown-900 mb-2">
                Récapitulatif et validation
              </h2>
              <p className="text-brown-600">
                Vérifiez les informations et créez le projet
              </p>
            </div>

            {/* Sélection du client */}
            <div className="p-4 bg-beige-50 rounded-lg">
              <label className="text-sm font-medium text-brown-700 mb-2 block">
                Client * (obligatoire pour créer le projet)
              </label>
              <Select
                value={state.client_id}
                onChange={(e) =>
                  setState((prev) => ({ ...prev, client_id: e.target.value }))
                }
              >
                <option value="">Sélectionnez un client</option>
                {clientsData?.clients?.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.full_name} {client.company ? `(${client.company})` : ''}
                  </option>
                ))}
              </Select>
            </div>

            {/* Document BMAD */}
            {state.bmad_markdown && (
              <div className="p-6 bg-white border border-beige-200 rounded-lg">
                <h3 className="text-lg font-semibold text-brown-900 mb-4 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-mint-600" />
                  Document BMAD généré
                </h3>
                <div className="prose prose-sm max-w-none">
                  <pre className="whitespace-pre-wrap text-sm text-brown-700 bg-beige-50 p-4 rounded">
                    {state.bmad_markdown}
                  </pre>
                </div>
              </div>
            )}

            {/* Score de complétude */}
            <div className="p-4 bg-mint-50 rounded-lg flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-mint-600" />
              <div>
                <p className="font-medium text-brown-900">
                  Projet {Math.round(state.completeness_score * 100)}% complet
                </p>
                <p className="text-sm text-brown-600">
                  Prêt à être créé et assigné au client
                </p>
              </div>
            </div>

            <div className="flex justify-between pt-6 border-t border-beige-200">
              <Button
                variant="outline"
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    step: 'dynamic_questions',
                  }))
                }
              >
                Retour
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!state.client_id || createProject.isPending}
                leftIcon={
                  createProject.isPending ? <Spinner size="sm" /> : <FolderKanban className="w-4 h-4" />
                }
              >
                {createProject.isPending ? 'Création...' : 'Créer le projet'}
              </Button>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Projets', href: '/projects' },
          { label: 'Nouveau projet avec IA' },
        ]}
      />

      {/* Back button */}
      <Link to="/projects">
        <Button variant="ghost" size="sm" leftIcon={<ArrowLeft className="w-4 h-4" />}>
          Retour
        </Button>
      </Link>

      {/* Auto-save indicator */}
      {draftId && (
        <div className="flex items-center justify-end gap-2 text-sm text-brown-600">
          {isSaving ? (
            <>
              <Spinner size="sm" />
              <span>Sauvegarde en cours...</span>
            </>
          ) : lastSaved ? (
            <>
              <Save className="w-4 h-4 text-mint-600" />
              <span>
                Dernière sauvegarde: {lastSaved.toLocaleTimeString()}
              </span>
            </>
          ) : null}
        </div>
      )}

      <Card padding="lg">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-mint-100 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-mint-600" />
            </div>
            <div>
              <CardTitle>Nouveau projet avec assistant IA</CardTitle>
              <p className="text-sm text-brown-600 mt-1">
                Créez un projet guidé par l'intelligence artificielle
              </p>
            </div>
          </div>
        </CardHeader>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-900">Erreur</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {loadingClients ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : (
          renderStepContent()
        )}
      </Card>
    </div>
  )
}
